/* eslint-disable no-unused-vars, jsx-a11y/anchor-is-valid, jsx-a11y/alt-text, eqeqeq, react-hooks/exhaustive-deps */
// MeetingRoomsEvent.jsx — RON-5051 container for the side-by-side room view.
//
// All data comes from ONE call, MeetingRoomsAvailableAndBookings (ZoneId "0"):
//   AvailableRooms[]                      -> every meeting room across all zones,
//                                            each carrying its own zone/map info
//   AvailableRooms[].SelectedBookingTime  -> EVERY meeting in that room (incl.
//                                            other people's), with MeetingId
//   Bookings[]                            -> the signed-in user's own meetings
// Joining Bookings[] to SelectedBookingTime on MeetingId is what makes
// Cancel/Edit possible; meetings without a match stay non-actionable.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";

import MeetingRoomsUI from "./MeetingRoomsUI";
import { MeetingRoomsAvailableAndBookings_api } from "./MeetingRoomsAPI";
import MeetingEventModal from "./MeetingEventModal";
import MeetingCancelDeclineModal from "./MeetingCancelDeclineModal";
import NewMeetingModal from "../Common/NewMeetingModal";
import NotificationMessage from "../Common/NotificationMessage";
import { FetchZoneList_api, CancelMeeting_api, DeclineMeeting_api } from "../WeeklyCalendar/WeeklyCalendarAPI";
import { formatDateYYYYMMDD, delay } from "../../utils/CommonUtils.js";
import { getFirstname, getLastname } from "../../utils/SecurityService.js";
import { useApiUiResponse } from "../../hooks/apiUiResponseHelper.js";

const SLOT_MINUTES = 30;
const DEFAULT_GRID_START_MIN = 8 * 60;
const DEFAULT_GRID_END_MIN = 19 * 60;

/* --------------------------------- helpers --------------------------------- */

export const timeToMinutes = (value) => {
    const [hours, minutes] = String(value || "").split(":").map(Number);
    if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
    return (hours * 60) + minutes;
};

const minutesToTime = (minutes) =>
    `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`;

// "13:30 - 14:00" -> { startMin: 810, endMin: 840 }
export const parseSlotRange = (slot) => {
    const [start, end] = String(slot || "").split("-").map((part) => part.trim());
    const startMin = timeToMinutes(start);
    const endMin = timeToMinutes(end);
    if (startMin === null || endMin === null) return null;
    return { startMin, endMin };
};

// Collapse a list of 30-minute slot strings into contiguous ranges. The API
// already groups a meeting's slots, but it splits non-contiguous runs into
// separate entries, so we merge defensively rather than trusting min/max.
export const mergeSlotRanges = (slots = []) => {
    const ranges = slots
        .map(parseSlotRange)
        .filter(Boolean)
        .sort((a, b) => a.startMin - b.startMin);

    return ranges.reduce((merged, range) => {
        const previous = merged[merged.length - 1];
        if (previous && range.startMin <= previous.endMin) {
            previous.endMin = Math.max(previous.endMin, range.endMin);
            return merged;
        }
        merged.push({ ...range });
        return merged;
    }, []);
};

// "A, B, C" -> ["A", "B", "C"], dropping blanks.
const splitList = (value) =>
    String(value ?? "")
        .split(",")
        .map((part) => part.trim())
        .filter(Boolean);

// "Munaf 2 Mansuri" -> "MM": first letter of the first and last word.
const initialsFromName = (name) => {
    const words = name.split(/\s+/).filter(Boolean);
    if (words.length === 0) return "";
    const first = words[0][0];
    const last = words.length > 1 ? words[words.length - 1][0] : "";
    return `${first}${last}`.toUpperCase();
};

// SelectedBookingTime's ParticipantNames / ParticipantInitials are two parallel
// comma-separated strings ("Munaf 2 Mansuri, Jahanvi S jadav" / "MM, JJ"), paired
// by position. Names drive the list: no names -> no participants, so nothing is
// rendered. A missing or short initials list falls back to initials built from
// the name rather than dropping the person.
export const parseParticipants = (names, initials) => {
    const nameList = splitList(names);
    const initialList = splitList(initials);

    return nameList.map((name, index) => ({
        name,
        initials: initialList[index] || initialsFromName(name),
    }));
};

const floorToHour = (minutes) => Math.floor(minutes / 60) * 60;
const ceilToHour = (minutes) => Math.ceil(minutes / 60) * 60;

// One room from AvailableRooms[] -> the column shape MeetingRoomsUI renders.
// `bookingsById` indexes the user's own Bookings[] by MeetingId.
const buildRoom = (room, bookingsById, dateStr) => {
    // A room past its own booking horizon isn't bookable, even when the
    // page-level LastBookingDate (the max across rooms) still allows the date.
    const isWithinHorizon = !room.LastBookingDate || dateStr <= room.LastBookingDate;

    // Each SelectedBookingTime entry is one meeting; merge its slots defensively
    // and attach the user's own booking row when the MeetingId matches.
    const meetings = (room.SelectedBookingTime || []).flatMap((entry, entryIndex) => {
        const booking = bookingsById.get(Number(entry.MeetingId)) || null;
        // ParticipantNames / ParticipantInitials (MeetingRoomsAvailableAndBookings):
        // read from the meeting entry, falling back to the user's own joined
        // Bookings[] row. No names in either -> [] -> nothing is rendered.
        const participantSource = entry.ParticipantNames ? entry : booking;
        const participants = parseParticipants(
            participantSource?.ParticipantNames,
            participantSource?.ParticipantInitials
        );

        return mergeSlotRanges(entry.BookedSlot).map((range, rangeIndex) => ({
            id: `MR-${room.SpotId}-${entry.MeetingId ?? entryIndex}-${rangeIndex}-${range.startMin}`,
            meetingId: entry.MeetingId ?? null,
            meetingName: entry.MeetingName || "",
            organizer: entry.Organizer || "",
            isOrganiser: Number(entry.IsOrganiser) === 1,
            participants,
            startMin: range.startMin,
            endMin: range.endMin,
            startTime: minutesToTime(range.startMin),
            endTime: minutesToTime(range.endMin),
            booking,
        }));
    });

    return {
        spotId: room.SpotId,
        spotName: room.SpotName,
        capacity: room.Capacity,
        zoneId: room.ZoneId,
        zoneName: room.ZoneName,
        mapExist: Number(room.MapExist),
        mapType: Number(room.MapType),
        startMin: timeToMinutes(room.StartBookingTime) ?? DEFAULT_GRID_START_MIN,
        endMin: timeToMinutes(room.EndBookingTime) ?? DEFAULT_GRID_END_MIN,
        isDateAvailable: Number(room.IsRoomAvailable) === 1 && isWithinHorizon,
        approvalInfo: room.ApprovalInfo || "",
        isApproversExist: room.IsApproversExist,
        blockedRanges: mergeSlotRanges(room.BlockedBookingTime),
        isFullDayBlocked: Number(room.IsFullDayBlocked) === 1,
        meetings,
    };
};

// Records of MeetingRoomsAvailableAndBookings -> rooms with per-column masks
// plus the shared grid bounds. Pure, so it is unit tested directly.
export const buildRoomsFromRecords = (records, dateStr) => {
    const bookingsById = new Map();
    (records?.Bookings || []).forEach((booking) => {
        bookingsById.set(Number(booking.MeetingId), booking);
    });

    const rooms = (records?.AvailableRooms || []).map((room) => buildRoom(room, bookingsById, dateStr));

    // Grid spans the union of every room's window, hour-aligned so the 48px
    // hour labels line up with the 24px gridlines.
    const gridStartMin = rooms.length
        ? floorToHour(Math.min(...rooms.map((room) => room.startMin)))
        : DEFAULT_GRID_START_MIN;
    const gridEndMin = rooms.length
        ? ceilToHour(Math.max(...rooms.map((room) => room.endMin)))
        : DEFAULT_GRID_END_MIN;

    // Per-column masks: out-of-hours, admin-blocked, or the whole column when
    // the room isn't bookable on this date at all.
    const roomsWithMasks = rooms.map((room) => {
        if (!room.isDateAvailable || room.isFullDayBlocked) {
            return { ...room, unavailableRanges: [{ startMin: gridStartMin, endMin: gridEndMin }] };
        }

        const ranges = [];
        if (room.startMin > gridStartMin) ranges.push({ startMin: gridStartMin, endMin: room.startMin });
        if (room.endMin < gridEndMin) ranges.push({ startMin: room.endMin, endMin: gridEndMin });
        ranges.push(...room.blockedRanges);

        return { ...room, unavailableRanges: ranges };
    });

    return { rooms: roomsWithMasks, gridStartMin, gridEndMin };
};

function MeetingRoomsEvent() {
    const { t, i18n } = useTranslation("common");
    const datePickerLocale = i18n.language.split("-")[0];
    // Same display name the weekly popup shows for the signed-in user.
    const userName = `${getFirstname() ?? ""} ${getLastname() ?? ""}`.trim();
    const history = useHistory();
    const { handleApiUiResponse, renderApiUi } = useApiUiResponse(t);

    const [isLoading, setIsLoading] = useState(false);
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [zones, setZones] = useState([]);
    const [zoneDropdownOpen, setZoneDropdownOpen] = useState(false);

    const [rooms, setRooms] = useState([]);
    // False until the first rooms request settles, so the empty state isn't shown
    // for the initial `[]` while the request is still in flight.
    const [isRoomsLoaded, setIsRoomsLoaded] = useState(false);
    const [gridStartMin, setGridStartMin] = useState(DEFAULT_GRID_START_MIN);
    const [gridEndMin, setGridEndMin] = useState(DEFAULT_GRID_END_MIN);
    const [holidays, setHolidays] = useState([]);
    const [lastBookingDate, setLastBookingDate] = useState(null);
    const [isThumbnailPresent, setIsThumbnailPresent] = useState("");
    const [companyId, setCompanyId] = useState("");

    const [selectedMeeting, setSelectedMeeting] = useState(null);
    const [showMeetingModal, setShowMeetingModal] = useState(false);
    // "cancel" | "decline" while the confirm popup is open, else null.
    const [meetingActionMode, setMeetingActionMode] = useState(null);

    const [meetingModalMode, setMeetingModalMode] = useState("create");
    const [showNewMeetingModal, setShowNewMeetingModal] = useState(false);
    const [newMeetingContext, setNewMeetingContext] = useState(null);
    const [editRescheduleType, setEditRescheduleType] = useState(2);
    const [notification, setNotification] = useState(null);

    // A slower earlier date must never overwrite the results of a newer one.
    const activeRequestIdRef = useRef(0);
    // Successful responses keyed by date, so revisiting a date costs nothing.
    // Cleared whenever a booking is created, edited or cancelled.
    const responseCacheRef = useRef(new Map());

    useEffect(() => {
        document.title = `${t("react_all_meeting_rooms")} | Ronspot`;
    }, [t]);

    /* --------------------------------- zones --------------------------------- */

    useEffect(() => {
        const loadZones = async () => {
            try {
                setIsLoading(true);
                const response = await FetchZoneList_api({});
                handleApiUiResponse(response, { successToaster: 0, errorToaster: 1, alertPopup: 1 });
                setZones(response?.Data?.Records?.ZoneList || []);
            } catch (error) {
                console.error("MeetingRooms - zone list fetch failed:", error);
            } finally {
                setIsLoading(false);
            }
        };
        loadZones();
    }, []);

    /* ---------------------------- rooms + meetings ---------------------------- */

    // One request per date: ZoneId "0" returns every meeting room across zones,
    // so this no longer waits on (or depends on) the zone list.
    const loadRooms = useCallback(async (date, { force = false } = {}) => {
        const requestId = activeRequestIdRef.current + 1;
        activeRequestIdRef.current = requestId;

        const dateStr = formatDateYYYYMMDD(date);
        setIsLoading(true);

        try {
            let response = force ? null : responseCacheRef.current.get(dateStr);
            if (!response) {
                response = await MeetingRoomsAvailableAndBookings_api({ BookingDate: dateStr });
                if (requestId !== activeRequestIdRef.current) return;
                handleApiUiResponse(response, { successToaster: 0, errorToaster: 1, alertPopup: 1 });
            }
            if (requestId !== activeRequestIdRef.current) return;

            const records = response?.Data?.Records;
            if (Number(response?.Data?.ResponseCode) !== 200 || !records) {
                setRooms([]);
                return;
            }
            responseCacheRef.current.set(dateStr, response);

            const next = buildRoomsFromRecords(records, dateStr);

            setRooms(next.rooms);
            setGridStartMin(next.gridStartMin);
            setGridEndMin(next.gridEndMin);
            setHolidays(Array.isArray(records.HolidaysZoneWise) ? records.HolidaysZoneWise : []);
            setLastBookingDate(records.LastBookingDate ? new Date(`${records.LastBookingDate}T00:00:00`) : null);
            setIsThumbnailPresent(records.IsThumbnailPresent ?? "");
            setCompanyId(records.CompanyId ?? "");
        } catch (error) {
            console.error("MeetingRooms - room load failed:", error);
            if (requestId === activeRequestIdRef.current) setRooms([]);
        } finally {
            if (requestId === activeRequestIdRef.current) {
                setIsLoading(false);
                setIsRoomsLoaded(true);
            }
        }
    }, []);

    useEffect(() => {
        loadRooms(selectedDate);
    }, [selectedDate, loadRooms]);

    // A booking changed, so every cached date is stale.
    const refreshRooms = () => {
        responseCacheRef.current.clear();
        return loadRooms(selectedDate, { force: true });
    };

    /* -------------------------------- handlers -------------------------------- */

    // "All meeting rooms" is a view, not a zone: picking a real zone hands the
    // user back to the monthly calendar with that zone selected.
    const handleZoneSelect = (zone) => {
        history.push("/calendar", { selectedZoneId: zone.Id });
    };

    const handleSlotSelect = ({ room, startTime, endTime }) => {
        setSelectedMeeting(null);
        setShowMeetingModal(false);
        setMeetingModalMode("create");
        setNewMeetingContext({ room, startTime, endTime });
        setShowNewMeetingModal(true);
    };

    const handleMeetingClick = ({ room, meeting }) => {
        setMeetingActionMode(null);
        setSelectedMeeting({ room, meeting });
        setShowMeetingModal(true);
    };

    // Map info arrives per room, so Go to map is always resolved from the
    // clicked room's own zone, never a page-level flag.
    const canRoomGoToMap = (room) => room?.mapExist === 1 && room?.mapType !== 1;

    const handleGoToMap = (room) => {
        if (!canRoomGoToMap(room)) return;

        history.push("/map", {
            selectedZoneId: room.zoneId,
            selectedDate: formatDateYYYYMMDD(selectedDate),
        });
    };

    const handleEditMeeting = (room, meeting, rescheduleType) => {
        setShowMeetingModal(false);
        setMeetingModalMode("edit");
        setEditRescheduleType(rescheduleType);
        setNewMeetingContext({
            room,
            startTime: meeting.startTime,
            endTime: meeting.endTime,
            meetingId: meeting.booking?.MeetingId,
        });
        setShowNewMeetingModal(true);
    };

    // Opens the Cancel / Decline confirm popup in place of the booking popup,
    // the same hand-off WeeklyCalendarEvent does.
    const openMeetingAction = (mode) => {
        setShowMeetingModal(false);
        setMeetingActionMode(mode);
    };

    const closeMeetingAction = () => {
        setMeetingActionMode(null);
        setSelectedMeeting(null);
    };

    // Cancel (organiser) or Decline (participant). `select` / `date` come from
    // MeetingCancelDeclineModal: 1 + StartDate for a one-off or whole series,
    // 2 + the picked date for a single occurrence.
    const handleMeetingAction = async (select, date) => {
        const booking = selectedMeeting?.meeting?.booking;
        if (!booking || !meetingActionMode || isLoading) return;

        const isCancel = meetingActionMode === "cancel";
        setIsLoading(true);
        try {
            const response = isCancel
                ? await CancelMeeting_api({ BookingId: booking.MeetingId, CancelDate: date, CancelSelect: select })
                : await DeclineMeeting_api({ BookingId: booking.MeetingId, DeclineDate: date, DeclineSelect: select });

            handleApiUiResponse(response, { successToaster: 1, errorToaster: 1, alertPopup: 1 });

            if (Number(response?.Data?.ResponseCode) === 200) {
                closeMeetingAction();
                await delay(1000);
                await refreshRooms();
            }
        } catch (error) {
            console.error(`MeetingRooms - ${isCancel ? "cancel" : "decline"} meeting failed:`, error);
        } finally {
            setIsLoading(false);
        }
    };

    // Zone list first; rooms fill any zone it hasn't delivered yet, since rooms
    // now load in parallel with (not after) the zone list.
    const zoneMapForModal = useMemo(() => {
        const map = {};
        rooms.forEach((room) => {
            if (Number.isFinite(room.mapType)) map[room.zoneId] = room.mapType;
        });
        zones.forEach((zone) => {
            map[zone.Id] = Number(zone.MapType ?? zone.Maptype ?? 2);
        });
        return map;
    }, [zones, rooms]);

    return (
        <>
            <MeetingRoomsUI
                isLoading={isLoading}
                isRoomsLoaded={isRoomsLoaded}
                rooms={rooms}
                gridStartMin={gridStartMin}
                gridEndMin={gridEndMin}
                selectedDate={selectedDate}
                setSelectedDate={setSelectedDate}
                lastBookingDate={lastBookingDate}
                holidays={holidays}
                zones={zones}
                zoneDropdownOpen={zoneDropdownOpen}
                setZoneDropdownOpen={setZoneDropdownOpen}
                onZoneSelect={handleZoneSelect}
                onMeetingClick={handleMeetingClick}
                onSlotSelect={handleSlotSelect}
                isThumbnailPresent={isThumbnailPresent}
                companyId={companyId}
            />

            {showMeetingModal && selectedMeeting && (
                <MeetingEventModal
                    room={selectedMeeting.room}
                    meeting={selectedMeeting.meeting}
                    selectedDate={selectedDate}
                    userName={userName}
                    isLoading={isLoading}
                    onClose={() => {
                        setShowMeetingModal(false);
                        setSelectedMeeting(null);
                    }}
                    onGoToMap={handleGoToMap}
                    onEdit={(rescheduleType) =>
                        handleEditMeeting(selectedMeeting.room, selectedMeeting.meeting, rescheduleType)
                    }
                    onCancel={() => openMeetingAction("cancel")}
                    onDecline={() => openMeetingAction("decline")}
                />
            )}

            {meetingActionMode && selectedMeeting && (
                <MeetingCancelDeclineModal
                    mode={meetingActionMode}
                    meeting={selectedMeeting.meeting}
                    selectedDate={selectedDate}
                    userName={userName}
                    holidays={holidays}
                    locale={datePickerLocale}
                    isLoading={isLoading}
                    onClose={closeMeetingAction}
                    onSubmit={handleMeetingAction}
                />
            )}

            {showNewMeetingModal && newMeetingContext && (
                <NewMeetingModal
                    // keyed on the edit type so "Open series" (3 -> 1) remounts a fresh modal (as MyBookingUI)
                    key={`meeting-modal-${meetingModalMode}-${editRescheduleType}`}
                    show={showNewMeetingModal}
                    isLoading={isLoading}
                    setIsLoading={setIsLoading}
                    date={selectedDate}
                    startTime={newMeetingContext.startTime}
                    endTime={newMeetingContext.endTime}
                    slotMinTime={`${String(Math.floor(gridStartMin / 60)).padStart(2, "0")}:00:00`}
                    slotMaxTime={`${String(Math.floor(gridEndMin / 60)).padStart(2, "0")}:00:00`}
                    selectedZone={newMeetingContext.room?.zoneId}
                    zoneMap={zoneMapForModal}
                    mapType={zoneMapForModal[newMeetingContext.room?.zoneId]}
                    navSpotId={newMeetingContext.room?.spotId}
                    setNotification={setNotification}
                    setShowScheduleMeetingModal={setShowNewMeetingModal}
                    mode={meetingModalMode}
                    editMeetingId={newMeetingContext.meetingId}
                    rescheduleType={editRescheduleType}
                    onOpenSeries={() => setEditRescheduleType(1)}
                    onClose={() => setShowNewMeetingModal(false)}
                    onConfirm={() => {
                        setShowNewMeetingModal(false);
                        refreshRooms();
                    }}
                    onMeetingCreated={() => {
                        setShowNewMeetingModal(false);
                        refreshRooms();
                    }}
                />
            )}

            {renderApiUi(isLoading)}

            {/* Toaster raised by NewMeetingModal via setNotification (it unmounts on close). */}
            {notification && !isLoading && (
                <NotificationMessage
                    type={notification.type}
                    title={notification.title}
                    message={notification.message}
                    onClose={() => setNotification(null)}
                />
            )}
        </>
    );
}

export default MeetingRoomsEvent;
